import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  PlusIcon, 
  MagnifyingGlassIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  DocumentTextIcon,
  EyeIcon,
  BuildingOffice2Icon,
  PencilSquareIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { Consultant } from '../types/consultant';
import { getAllConsultants, deleteConsultant } from '../services/consultantService';
import toast from 'react-hot-toast';
import ConsultantForm from '../components/consultants/ConsultantForm';
import ConsultantDetailView from '../components/consultants/ConsultantDetailView';
import CommonPageHeader from '../components/common/CommonPageHeader';
import SearchBar from '../components/common/SearchBar';

const Consultants: React.FC = () => {
  const { t } = useLanguage();
  const [consultants, setConsultants] = useState<Consultant[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showDetailView, setShowDetailView] = useState(false);
  const [selectedConsultant, setSelectedConsultant] = useState<Consultant | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [consultantToDelete, setConsultantToDelete] = useState<Consultant | null>(null);

  useEffect(() => {
    loadConsultants();
  }, []);

  const loadConsultants = async () => {
    try {
      setLoading(true);
      const data = await getAllConsultants();
      setConsultants(data);
    } catch (error) {
      console.error('Error loading consultants:', error);
      toast.error('Hiba a szakreferensek betöltése során');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setSelectedConsultant(null);
    setShowForm(true);
  };

  const handleView = (consultant: Consultant) => {
    setSelectedConsultant(consultant);
    setShowDetailView(true);
  };

  const handleEdit = (consultant: Consultant) => {
    setSelectedConsultant(consultant);
    setShowForm(true);
  };

  const handleDelete = (consultant: Consultant) => {
    setConsultantToDelete(consultant);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!consultantToDelete) return;

    try {
      await deleteConsultant(consultantToDelete.id);
      toast.success('Szakreferens sikeresen törölve');
      loadConsultants();
      setShowDeleteModal(false);
      setConsultantToDelete(null);
    } catch (error) {
      console.error('Error deleting consultant:', error);
      toast.error('Hiba a szakreferens törlése során');
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setSelectedConsultant(null);
    loadConsultants();
  };

  const handleDetailViewClose = () => {
    setShowDetailView(false);
    setSelectedConsultant(null);
    loadConsultants();
  };

  const filteredConsultants = consultants.filter(consultant =>
    consultant.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    consultant.contacts.some(contact => 
      contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.email.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-[100vw] px-2 sm:px-0 overflow-x-hidden">
      {/* Header */}
      <CommonPageHeader
        title="Szakreferensek"
        subtitle="Energetikus cégek és kapcsolattartóik kezelése"
        icon={BuildingOffice2Icon}
        onRefresh={loadConsultants}
        showLastUpdate={false}
      >
        <button
          onClick={handleAdd}
          className="inline-flex items-center px-4 py-2 bg-primary-500/90 backdrop-blur-sm text-white rounded-lg hover:bg-primary-600/90 transition-colors"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          Új szakreferens
        </button>
      </CommonPageHeader>

      {/* Search */}
      <SearchBar
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder="Keresés cégnév, kapcsolattartó neve vagy email alapján..."
      />

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <BuildingOfficeIcon className="h-8 w-8 text-blue-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Összes szakreferens</p>
              <p className="text-2xl font-semibold text-gray-900">{consultants.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <UserGroupIcon className="h-8 w-8 text-green-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Kezelt ügyfelek</p>
              <p className="text-2xl font-semibold text-gray-900">
                {consultants.reduce((sum, c) => sum + c.managedCustomers.length, 0)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <DocumentTextIcon className="h-8 w-8 text-purple-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Meghatalmazások</p>
              <p className="text-2xl font-semibold text-gray-900">
                {consultants.reduce((sum, c) => sum + c.authorizationFiles.length, 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Consultants List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {filteredConsultants.length === 0 ? (
          <div className="text-center py-12">
            <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nincs szakreferens</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? 'Nincs találat a keresési feltételeknek megfelelően.' : 'Kezdj el egy új szakreferens hozzáadásával.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cégnév
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kapcsolattartók
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kezelt ügyfelek
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Meghatalmazások
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Műveletek
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredConsultants.map((consultant) => (
                  <tr key={consultant.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <BuildingOfficeIcon className="h-5 w-5 text-gray-400 mr-2" />
                        <div>
                          <button
                            onClick={() => handleView(consultant)}
                            className="text-sm font-medium text-gray-900 hover:text-primary-600 hover:underline transition-colors text-left"
                            title="Részletek megtekintése"
                          >
                            {consultant.companyName}
                          </button>
                          {consultant.taxNumber && (
                            <div className="text-xs text-gray-500">
                              Adószám: {consultant.taxNumber}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {consultant.contacts.length > 0 ? (
                          <div className="space-y-1">
                            {consultant.contacts.slice(0, 2).map(contact => (
                              <div key={contact.id} className="flex items-center">
                                <span className="font-medium">{contact.name}</span>
                                {contact.isPrimary && (
                                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                    Elsődleges
                                  </span>
                                )}
                              </div>
                            ))}
                            {consultant.contacts.length > 2 && (
                              <div className="text-xs text-gray-500">
                                +{consultant.contacts.length - 2} további
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">Nincs kapcsolattartó</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <UserGroupIcon className="h-5 w-5 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900">
                          {consultant.managedCustomers.length} ügyfél
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900">
                          {consultant.authorizationFiles.length} fájl
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleView(consultant)}
                          className="text-gray-600 hover:text-primary-600"
                          title="Részletek"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(consultant)}
                          className="text-gray-600 hover:text-primary-600"
                          title="Szerkesztés"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(consultant)}
                          className="text-gray-600 hover:text-red-600"
                          title="Törlés"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail View Modal */}
      {showDetailView && selectedConsultant && (
        <ConsultantDetailView
          consultant={selectedConsultant}
          onClose={handleDetailViewClose}
        />
      )}

      {/* Form Modal */}
      <ConsultantForm
        isOpen={showForm}
        consultant={selectedConsultant}
        onClose={handleFormClose}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && consultantToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Szakreferens törlése
              </h2>
              <p className="text-sm text-gray-600">
                Biztosan törölni szeretnéd ezt a szakreferenst?
              </p>
              <p className="text-sm font-medium text-gray-900 mt-2">
                {consultantToDelete.companyName}
              </p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-yellow-800">
                <strong>⚠️ Figyelem!</strong> Ez a művelet nem vonható vissza. A szakreferenshez tartozó kapcsolattartók és hozzárendelések is törlődnek.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setConsultantToDelete(null);
                }}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Mégse
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 bg-red-600 text-white px-4 py-3 rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Törlés
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Consultants;
